import {
  ATTACHMENT_ACTION_PROVIDER_TOKEN,
  IAttachmentActionProvider,
} from '@integration-frontends/integration/ui/attachment-selector/common/attachment-actions/attachment-actions-model';
import { AuthenticationServiceTokenStrategy } from '@integration-frontends/common/auth/core/infrastructure';
import {
  AUTHENTICATE_SERVICE_TOKEN,
  IAuthenticateService,
} from '@integration-frontends/common/auth/core/model';
import { AuthUiModule } from '@integration-frontends/common/auth/ui';
import {
  BRANDFOLDER_API_TOKEN,
  BrandfolderApi,
} from '@integration-frontends/common/brandfolder-api';
import {
  BFHistory,
  BFStore,
  COMPONENT_RESOLVER_TOKEN,
  ComponentResolver,
  DI_CONTAINER,
  HISTORY_TOKEN,
  ILogger,
  LOGGER_TOKEN,
  STORE_TOKEN,
  IObservabilityService,
  ObservabilityServiceDatadogStrategy,
  OBSERVABILITY_SERVICE_TOKEN,
  IAnalyticsService,
  ANALYTICS_SERVICE_TOKEN,
  AnalyticsServiceStrategy,
} from '@integration-frontends/core';
import {
  ISelectAttachmentOptions,
  IUploadAssetsOptions,
  SELECT_ATTACHMENT_OPTIONS_TOKEN,
  SelectActionType,
  SelectionType,
  UPLOAD_ASSETS_OPTIONS_TOKEN,
} from '@integration-frontends/integration/core/application';
import {
  ALL_IMAGE_TYPES,
  ASSET_REPO_TOKEN,
  ATTACHMENT_REPO_TOKEN,
  ATTACHMENT_TRANSFORM_SERVICE_TOKEN,
  BRANDFOLDER_REPO_TOKEN,
  BRANDFOLDER_WEBSITE_SERVICE_TOKEN,
  CDN_SERVICE_TOKEN,
  COLLECTION_REPO_TOKEN,
  CONTAINER_REPO_TOKEN,
  DOWNLOAD_SERVICE_TOKEN,
  IAssetRepo,
  IAttachmentRepo,
  IAttachmentTransformService,
  IBrandfolderRepo,
  IBrandfolderWebsiteService,
  ICdnService,
  ICollectionRepo,
  IContainerRepo,
  IDownloadService,
  ILabelRepo,
  ImageType,
  IMediaTypeSupportService,
  IOrganizationRepo,
  ISearchFilterRepo,
  ISectionRepo,
  ISharingService,
  IUserSettingsRepo,
  LABEL_REPO_TOKEN,
  MEDIA_TYPE_SUPPORT_SERVICE_TOKEN,
  ORGANIZATION_REPO_TOKEN,
  SEARCH_FILTER_REPO_TOKEN,
  SECTION_REPO_TOKEN,
  SHARING_SERVICE_TOKEN,
  USER_SETTINGS_REPO_TOKEN,
  VideoType,
} from '@integration-frontends/integration/core/model';
import {
  AssetRepo,
  AttachmentRepo,
  AttachmentSelectorFeaturesService,
  AttachmentTransformService,
  BrandfolderRepo,
  BrandfolderWebsiteService,
  CdnService,
  CollectionRepo,
  ContainerRepo,
  DownloadService,
  GET_API_KEY_TOKEN,
  getApiKeyIdentityStrategy,
  IGetAuthToken,
  LabelRepo,
  MediaTypeSupportServiceWhitelistStrategy,
  OrganizationRepo,
  SearchFilterRepo,
  SectionRepo,
  SharingService,
  UserSettingsRepo,
} from '@integration-frontends/integration/infrastructure/isomorphic';
import {
  ATTACHMENT_SELECTOR_FEATURES_TOKEN,
  AttachmentSelectorFeature,
  IAttachmentSelectorFeaturesService,
  IntegrationUiModule,
} from '@integration-frontends/integration/ui';
import { Container } from 'inversify';
import { difference } from 'ramda';
import {
  IDENTITY_STORE_TOKEN,
  IdentityStoreLocalStorageStrategy,
  IIdentityStore,
} from './app/common';
import { BF_BASE_URL, BF_STATIC_ASSETS_BASE_URL, BF_WEBSITE_BASE_URL } from './config';
import './i18n';
import { AttachmentActionsProviderPanelSFMCStrategy } from './app/infrastructure/attachment-actions-provider-sfmc-strategy';
import { createStore } from './app/store';
import { ObservabilityUiModule } from '@integration-frontends/core/ui';

// TODO: use BFModule. ~PP

/**
 * App
 */
const appContainer = new Container();
appContainer.bind<ILogger>(LOGGER_TOKEN).toConstantValue(console);
appContainer.bind<IIdentityStore>(IDENTITY_STORE_TOKEN).to(IdentityStoreLocalStorageStrategy);
DI_CONTAINER.merge(appContainer);

/**
 * Store
 */
const storeContainer = new Container();
const { store, history } = createStore();
storeContainer.bind<BFStore>(STORE_TOKEN).toConstantValue(store);
storeContainer.bind<BFHistory>(HISTORY_TOKEN).toConstantValue(history);
DI_CONTAINER.merge(storeContainer);

/**
 * Auth
 */
const authContainer = new Container();
authContainer
  .bind<IAuthenticateService>(AUTHENTICATE_SERVICE_TOKEN)
  .to(AuthenticationServiceTokenStrategy);

AuthUiModule.initialize(authContainer);

/**
 * Integration
 */
const integrationContainer = new Container();

integrationContainer
  .bind<BrandfolderApi>(BRANDFOLDER_API_TOKEN)
  .toConstantValue(new BrandfolderApi(BF_BASE_URL, DI_CONTAINER.get(LOGGER_TOKEN)));

integrationContainer
  .bind<IGetAuthToken>(GET_API_KEY_TOKEN)
  .toConstantValue(getApiKeyIdentityStrategy);
integrationContainer.bind<IAttachmentRepo>(ATTACHMENT_REPO_TOKEN).to(AttachmentRepo);
integrationContainer
  .bind<IAttachmentTransformService>(ATTACHMENT_TRANSFORM_SERVICE_TOKEN)
  .to(AttachmentTransformService);
integrationContainer.bind<IAssetRepo>(ASSET_REPO_TOKEN).to(AssetRepo);
integrationContainer.bind<IBrandfolderRepo>(BRANDFOLDER_REPO_TOKEN).to(BrandfolderRepo);
integrationContainer.bind<ICollectionRepo>(COLLECTION_REPO_TOKEN).to(CollectionRepo);
integrationContainer.bind<ISearchFilterRepo>(SEARCH_FILTER_REPO_TOKEN).to(SearchFilterRepo);
integrationContainer.bind<ISectionRepo>(SECTION_REPO_TOKEN).to(SectionRepo);
integrationContainer.bind<IOrganizationRepo>(ORGANIZATION_REPO_TOKEN).to(OrganizationRepo);
integrationContainer.bind<ILabelRepo>(LABEL_REPO_TOKEN).to(LabelRepo);
integrationContainer.bind<IContainerRepo>(CONTAINER_REPO_TOKEN).to(ContainerRepo);
integrationContainer.bind<IUserSettingsRepo>(USER_SETTINGS_REPO_TOKEN).to(UserSettingsRepo);
integrationContainer
  .bind<IMediaTypeSupportService>(MEDIA_TYPE_SUPPORT_SERVICE_TOKEN)
  .toConstantValue(
    new MediaTypeSupportServiceWhitelistStrategy(ALL_IMAGE_TYPES, [
      ...difference(ALL_IMAGE_TYPES, [ImageType.Ai, ImageType.svg]),
      VideoType.Mp4,
      VideoType.Ogg,
      VideoType.Webm,
    ]),
  );
integrationContainer.bind<IDownloadService>(DOWNLOAD_SERVICE_TOKEN).to(DownloadService);
integrationContainer.bind<ISharingService>(SHARING_SERVICE_TOKEN).to(SharingService);
integrationContainer
  .bind<IBrandfolderWebsiteService>(BRANDFOLDER_WEBSITE_SERVICE_TOKEN)
  .toConstantValue(new BrandfolderWebsiteService(BF_WEBSITE_BASE_URL, BF_STATIC_ASSETS_BASE_URL));
integrationContainer
  .bind<IAttachmentSelectorFeaturesService>(ATTACHMENT_SELECTOR_FEATURES_TOKEN)
  .toConstantValue(new AttachmentSelectorFeaturesService([AttachmentSelectorFeature.Responsive]));
integrationContainer
  .bind<ISelectAttachmentOptions>(SELECT_ATTACHMENT_OPTIONS_TOKEN)
  .toConstantValue({ actionType: SelectActionType.Place, selectionType: SelectionType.Single });
integrationContainer.bind<IUploadAssetsOptions>(UPLOAD_ASSETS_OPTIONS_TOKEN).toConstantValue({
  showAddMore: true,
  showTags: false,
});
integrationContainer
  .bind<IAttachmentActionProvider>(ATTACHMENT_ACTION_PROVIDER_TOKEN)
  .toConstantValue(new AttachmentActionsProviderPanelSFMCStrategy());
integrationContainer
  .bind<ComponentResolver>(COMPONENT_RESOLVER_TOKEN)
  .toConstantValue(new ComponentResolver());
integrationContainer.bind<ICdnService>(CDN_SERVICE_TOKEN).to(CdnService);

integrationContainer
  .bind<IAnalyticsService>(ANALYTICS_SERVICE_TOKEN)
  .to(AnalyticsServiceStrategy);

IntegrationUiModule.initialize(integrationContainer);

/** Observability
 */
const observabilityContainer = new Container();
observabilityContainer
  .bind<IObservabilityService>(OBSERVABILITY_SERVICE_TOKEN)
  .to(ObservabilityServiceDatadogStrategy);

ObservabilityUiModule.initialize(observabilityContainer);
